import axios from "axios";
import * as THREE from "three";
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { HDRLoader } from "three/examples/jsm/loaders/HDRLoader";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { LUTCubeLoader } from "three/examples/jsm/loaders/LUTCubeLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { TDSLoader } from "three/examples/jsm/loaders/TDSLoader";
import { loadTexture, loadTextureWithProps } from "./managers/texture.manager";
import AppData from "../../app.data";
import { createBoundingCylinder } from "../../utils/geometry.utils";
import { createAnimationEntry } from "../../utils/runtime-registry.utils";
import { ModelLoadSource, ModelSourceType } from "../../utils/general.utils";
import { fixFBXMaterials } from "../../utils/materials.utils";
import { createModelPackage, ModelPackage, ModelPackageUtils, ResolvedAssetFile } from "./model.package";
import { LOCAL_DRACO_DECODER_PATH } from "./draco.constants";
import { LOCAL_KTX2_TRANSCODER_PATH } from "./ktx2.constants";
import { createMutableAssetCacheBypassKey, getMutableManagedAssetUrl } from "../../utils/media-cache-policy.utils";
import { describeModelSource, redactAssetUrl, sanitizeAssetError } from "./loader.diagnostics";

const MODEL_MIXER_USER_DATA_KEY = "__artetraAnimationMixers";

/**
 * Asset loader class handling all resource loading operations
 * Manages loading of 3D models, textures, HDR environments, and audio files
 * Provides loading progress feedback and error handling
 */
export class Loader {
  private static instance: Loader | null = null;

  manager: THREE.LoadingManager;
  hdrLoader: HDRLoader;
  textureLoader: THREE.TextureLoader | null;
  audioLoader: THREE.AudioLoader;
  lutCubeLoader: LUTCubeLoader;

  private cachedTextures = {} as any;
  private appData = new AppData();
  private readonly modelLoaderRegistry: Record<string, (
    modelPackage: ModelPackage,
    isArtwork: boolean,
    renderer?: THREE.WebGLRenderer
  ) => Promise<any>> = {
    glb: this.loadGLTFPackage.bind(this),
    gltf: this.loadGLTFPackage.bind(this),
    fbx: this.loadFBXPackage.bind(this),
    obj: this.loadOBJPackage.bind(this),
    dae: this.loadDAEPackage.bind(this),
    ply: this.loadPLYPackage.bind(this),
    stl: this.loadSTLPackage.bind(this),
    "3ds": this.load3DSPackage.bind(this),
  };

  constructor() {
    if (Loader.instance) {
      return Loader.instance;
    }

    this.manager = new THREE.LoadingManager();
    this.configureManager(this.manager);

    this.hdrLoader = new HDRLoader(this.manager);
    this.hdrLoader.setCrossOrigin("anonymous");
    this.hdrLoader.setRequestHeader({
      "Cache-Control": "no-cache"
    });

    this.textureLoader = this.createTextureLoader();

    this.audioLoader = new THREE.AudioLoader(this.manager);
    this.audioLoader.setCrossOrigin("anonymous");

    this.lutCubeLoader = new LUTCubeLoader(this.manager);

    Loader.instance = this;
  }

  private createTextureLoader() {
    const textureLoader = new THREE.TextureLoader(this.manager);
    textureLoader.setCrossOrigin("anonymous");
    textureLoader.setRequestHeader({
      "Cache-Control": "no-cache"
    });
    return textureLoader;
  }

  private ensureTextureLoader() {
    if (!this.textureLoader) {
      this.textureLoader = this.createTextureLoader();
    }
    return this.textureLoader;
  }

  private withPmremShaderDiagnosticsSuppressed<T>(renderer: THREE.WebGLRenderer, operation: () => T): T {
    const debug = (renderer as any)?.debug;
    if (!debug || typeof debug.checkShaderErrors !== "boolean") {
      return operation();
    }

    const previousCheckShaderErrors = debug.checkShaderErrors;
    debug.checkShaderErrors = false;
    try {
      return operation();
    } finally {
      debug.checkShaderErrors = previousCheckShaderErrors;
    }
  }

  public static getInstance(): Loader {
    if (!Loader.instance) {
      Loader.instance = new Loader();
    }
    return Loader.instance;
  }

  private configureManager(manager: THREE.LoadingManager) {
    manager.onStart = function () { };
    manager.onLoad = function () { };
    manager.onProgress = function () { };
    manager.onError = function (url) {
      console.error("Asset load failed", { url: redactAssetUrl(url) });
    };
  }

  private cloneManager() {
    const manager = new THREE.LoadingManager();
    manager.onStart = this.manager.onStart;
    manager.onLoad = this.manager.onLoad;
    manager.onProgress = this.manager.onProgress;
    manager.onError = this.manager.onError;
    return manager;
  }

  private createGLTFLoader(manager: THREE.LoadingManager, renderer?: THREE.WebGLRenderer) {
    const gltfLoader = new GLTFLoader(manager);
    gltfLoader.setMeshoptDecoder(MeshoptDecoder);
    const dracoLoader = new DRACOLoader(manager);
    dracoLoader.setDecoderPath(LOCAL_DRACO_DECODER_PATH);
    dracoLoader.setWorkerLimit(2);
    dracoLoader.preload();
    gltfLoader.setDRACOLoader(dracoLoader);

    const activeRenderer = renderer || this.appData.renderService?.renderer;
    let ktx2Loader: KTX2Loader | null = null;
    if (activeRenderer) {
      ktx2Loader = new KTX2Loader(manager);
      ktx2Loader.setTranscoderPath(LOCAL_KTX2_TRANSCODER_PATH);
      ktx2Loader.setWorkerLimit(2);
      ktx2Loader.detectSupport(activeRenderer);
      gltfLoader.setKTX2Loader(ktx2Loader);
    }

    return { gltfLoader, dracoLoader, ktx2Loader };
  }

  private getVirtualResourcePath(filePath: string) {
    const directoryPath = ModelPackageUtils.getDirname(filePath);
    const virtualPath = ModelPackageUtils.toVirtualPath(directoryPath);
    return directoryPath ? `${virtualPath}/` : virtualPath;
  }

  private async withPackageUrlResolution<T>(
    modelPackage: ModelPackage,
    manager: THREE.LoadingManager,
    cacheBypassKey: string,
    callback: () => Promise<T>
  ) {
    manager.setURLModifier((requestedPath) => {
      if (!requestedPath) return requestedPath;
      if (requestedPath.startsWith(LOCAL_KTX2_TRANSCODER_PATH)) return requestedPath;

      const normalizedPath = requestedPath.startsWith(ModelPackageUtils.ZIP_VIRTUAL_ROOT)
        ? requestedPath.slice(ModelPackageUtils.ZIP_VIRTUAL_ROOT.length)
        : requestedPath;

      const resolvedUrl = modelPackage.resolveAssetUrl(normalizedPath) || requestedPath;
      return getMutableManagedAssetUrl(resolvedUrl, cacheBypassKey);
    });

    try {
      return await callback();
    } finally {
      manager.setURLModifier((url) => url);
    }
  }

  private async loadWithLoader<T>(
    label: string,
    url: string,
    loaderFn: (resolve: (value: T) => void, reject: (reason?: any) => void) => void
  ) {
    return new Promise<T>((resolve, reject) => {
      loaderFn(resolve, (error) => {
        console.error(`${label}: Error loading model`, { url: redactAssetUrl(url), error: sanitizeAssetError(error) });
        reject(error);
      });
    });
  }

  private applyCommonModelSettings(model: THREE.Object3D) {
    model.traverse((child: any) => {
      if (child instanceof THREE.Mesh && !child.name.includes("entryTo_Space")) {
        child.castShadow = true;
        child.receiveShadow = true;
      }

      const materials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
      materials.forEach((material: any) => {
        if (!material) return;
        material.dithering = true;
      });
    });
  }

  private geometryHasFinitePositions(geometry: THREE.BufferGeometry) {
    const position = geometry.getAttribute("position");
    const array = position?.array;
    if (!array || array.length === 0) {
      return false;
    }

    for (let index = 0; index < array.length; index++) {
      if (!Number.isFinite(array[index])) {
        return false;
      }
    }

    return true;
  }

  private inspectPLYHeader(arrayBuffer: ArrayBuffer) {
    const headerBytes = new Uint8Array(arrayBuffer.slice(0, Math.min(arrayBuffer.byteLength, 64 * 1024)));
    const headerText = new TextDecoder().decode(headerBytes);
    const endHeaderIndex = headerText.indexOf("end_header");

    if (endHeaderIndex < 0) {
      return {
        vertexCount: 0,
        faceCount: 0,
        propertyNames: new Set<string>(),
        isGaussianSplatLike: false,
      };
    }

    const header = headerText.slice(0, endHeaderIndex);
    const lines = header.split(/\r\n|\r|\n/);
    const propertyNames = new Set<string>();
    let currentElement = "";
    let vertexCount = 0;
    let faceCount = 0;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      if (trimmedLine.startsWith("element ")) {
        const [, elementName, countText] = trimmedLine.split(/\s+/);
        currentElement = elementName || "";
        const count = Number(countText);

        if (currentElement === "vertex" && Number.isFinite(count)) {
          vertexCount = count;
        }

        if (currentElement === "face" && Number.isFinite(count)) {
          faceCount = count;
        }
      }

      if (currentElement === "vertex" && trimmedLine.startsWith("property ")) {
        const parts = trimmedLine.split(/\s+/);
        const propertyName = parts[parts.length - 1];
        if (propertyName) {
          propertyNames.add(propertyName);
        }
      }
    }

    const isGaussianSplatLike =
      propertyNames.has("scale_0") ||
      propertyNames.has("rot_0") ||
      propertyNames.has("opacity") ||
      propertyNames.has("f_dc_0");

    return {
      vertexCount,
      faceCount,
      propertyNames,
      isGaussianSplatLike,
    };
  }

  private createPLYLoader(manager: THREE.LoadingManager) {
    const plyLoader = new PLYLoader(manager);
    plyLoader.setPropertyNameMapping({
      diffuse_red: "red",
      diffuse_green: "green",
      diffuse_blue: "blue",
    });
    plyLoader.setCustomPropertyNameMapping({
      splatColor: ["f_dc_0", "f_dc_1", "f_dc_2"],
      splatOpacity: ["opacity"],
      splatScale: ["scale_0", "scale_1", "scale_2"],
      splatRotation: ["rot_0", "rot_1", "rot_2", "rot_3"],
    });
    return plyLoader;
  }

  private applyGaussianSplatColorFallback(geometry: THREE.BufferGeometry) {
    if (geometry.getAttribute("color")) {
      return;
    }

    const splatColor = geometry.getAttribute("splatColor") as THREE.BufferAttribute | undefined;
    if (!splatColor || splatColor.itemSize < 3) {
      return;
    }

    const colorValues: number[] = [];
    const SH_C0 = 0.28209479177387814;
    for (let index = 0; index < splatColor.count; index++) {
      const r = Math.min(1, Math.max(0, 0.5 + SH_C0 * splatColor.getX(index)));
      const g = Math.min(1, Math.max(0, 0.5 + SH_C0 * splatColor.getY(index)));
      const b = Math.min(1, Math.max(0, 0.5 + SH_C0 * splatColor.getZ(index)));
      colorValues.push(r, g, b);
    }

    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colorValues, 3));
  }

  private estimatePointSizeFromGeometry(geometry: THREE.BufferGeometry) {
    const boundingBox = geometry.boundingBox;
    if (!boundingBox) {
      return 0.02;
    }

    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const maxDimension = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxDimension) || maxDimension <= 0) {
      return 0.02;
    }

    return Math.max(maxDimension / 200, 0.0025);
  }

  private createPLYRenderable(
    geometry: THREE.BufferGeometry,
    plyHeader: { faceCount: number; isGaussianSplatLike: boolean }
  ) {
    const shouldRenderAsPoints = plyHeader.faceCount === 0 || plyHeader.isGaussianSplatLike;

    if (shouldRenderAsPoints) {
      this.applyGaussianSplatColorFallback(geometry);
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      const pointsMaterial = new THREE.PointsMaterial({
        size: this.estimatePointSizeFromGeometry(geometry),
        sizeAttenuation: true,
        vertexColors: Boolean(geometry.getAttribute("color")),
        color: geometry.getAttribute("color") ? 0xffffff : 0xbfbfbf,
        transparent: true,
        opacity: 0.95,
      });

      return new THREE.Points(geometry, pointsMaterial);
    }

    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    return new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({
        color: 0xbfbfbf,
        vertexColors: Boolean(geometry.getAttribute("color")),
      })
    );
  }

  private normalizeColladaDocument(colladaText: string) {
    if (typeof DOMParser === "undefined" || typeof XMLSerializer === "undefined") {
      return colladaText;
    }

    try {
      const parser = new DOMParser();
      const xmlDocument = parser.parseFromString(colladaText, "application/xml");
      const parseError = xmlDocument.querySelector("parsererror");
      if (parseError) {
        return colladaText;
      }

      const profileCommons = Array.from(xmlDocument.getElementsByTagName("profile_COMMON"));
      profileCommons.forEach((profileCommon) => {
        const imageIds = new Set(
          Array.from(xmlDocument.getElementsByTagName("image"))
            .map((imageNode) => imageNode.getAttribute("id"))
            .filter(Boolean) as string[]
        );

        const surfaceIds = new Set<string>();
        const samplerIds = new Set<string>();

        Array.from(profileCommon.getElementsByTagName("newparam")).forEach((newParamNode) => {
          const sid = newParamNode.getAttribute("sid");
          if (!sid) return;

          if (newParamNode.getElementsByTagName("surface").length > 0) {
            surfaceIds.add(sid);
          }

          if (newParamNode.getElementsByTagName("sampler2D").length > 0) {
            samplerIds.add(sid);
          }
        });

        Array.from(profileCommon.getElementsByTagName("texture")).forEach((textureNode) => {
          const textureId = textureNode.getAttribute("texture");
          if (!textureId || samplerIds.has(textureId)) {
            return;
          }

          if (surfaceIds.has(textureId)) {
            const samplerId = `${textureId}-sampler`;
            if (!samplerIds.has(samplerId)) {
              const samplerParam = xmlDocument.createElement("newparam");
              samplerParam.setAttribute("sid", samplerId);

              const sampler2D = xmlDocument.createElement("sampler2D");
              const source = xmlDocument.createElement("source");
              source.textContent = textureId;
              sampler2D.appendChild(source);
              samplerParam.appendChild(sampler2D);
              profileCommon.appendChild(samplerParam);
              samplerIds.add(samplerId);
            }

            textureNode.setAttribute("texture", samplerId);
            return;
          }

          if (imageIds.has(textureId)) {
            const surfaceId = `${textureId}-surface`;
            const samplerId = `${textureId}-sampler`;

            if (!surfaceIds.has(surfaceId)) {
              const surfaceParam = xmlDocument.createElement("newparam");
              surfaceParam.setAttribute("sid", surfaceId);

              const surface = xmlDocument.createElement("surface");
              surface.setAttribute("type", "2D");

              const initFrom = xmlDocument.createElement("init_from");
              initFrom.textContent = textureId;
              surface.appendChild(initFrom);
              surfaceParam.appendChild(surface);
              profileCommon.appendChild(surfaceParam);
              surfaceIds.add(surfaceId);
            }

            if (!samplerIds.has(samplerId)) {
              const samplerParam = xmlDocument.createElement("newparam");
              samplerParam.setAttribute("sid", samplerId);

              const sampler2D = xmlDocument.createElement("sampler2D");
              const source = xmlDocument.createElement("source");
              source.textContent = surfaceId;
              sampler2D.appendChild(source);
              samplerParam.appendChild(sampler2D);
              profileCommon.appendChild(samplerParam);
              samplerIds.add(samplerId);
            }

            textureNode.setAttribute("texture", samplerId);
          }
        });
      });

      return new XMLSerializer().serializeToString(xmlDocument);
    } catch (error) {
      console.warn("Loader: Failed to normalize Collada document", error);
      return colladaText;
    }
  }

  private setupAnimationMixers(model: THREE.Object3D, animations?: THREE.AnimationClip[]) {
    if (!animations?.length) return;

    const mixer = new THREE.AnimationMixer(model);
    this.appData.skinnedMeshes.push(createAnimationEntry(mixer, model));
    const mixerDescriptor = Object.getOwnPropertyDescriptor(model.userData, MODEL_MIXER_USER_DATA_KEY);
    const registeredMixers = Array.isArray(mixerDescriptor?.value)
      ? mixerDescriptor.value
      : [];
    registeredMixers.push(mixer);
    Object.defineProperty(model.userData, MODEL_MIXER_USER_DATA_KEY, {
      value: registeredMixers,
      enumerable: false,
      configurable: true,
      writable: true,
    });
    mixer.clipAction(animations[0]).play();

    model.traverse((child: any) => {
      const materials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
      materials.forEach((material: any) => {
        if (!material) return;
        material.depthTest = true;
        material.depthWrite = true;
      });
    });

    const cylinder = createBoundingCylinder(model, animations);
    model.add(cylinder);
  }

  private finalizeLoadedModel(model: THREE.Object3D, type: ModelSourceType, animations?: THREE.AnimationClip[]) {
    if (type === "fbx") {
      fixFBXMaterials(model);
    }

    this.applyCommonModelSettings(model);
    this.setupAnimationMixers(model, animations);
    return model;
  }

  private findCompanionFile(modelPackage: ModelPackage, entryFile: ResolvedAssetFile, extension: string, preferredFile?: string | null) {
    if (preferredFile) {
      const preferredMatch = modelPackage.getFile(preferredFile);
      if (preferredMatch) {
        return preferredMatch;
      }
    }

    const entryDir = ModelPackageUtils.getDirname(entryFile.path);
    const entryStem = entryFile.name.replace(/\.[^.]+$/, "");
    const siblingMatch = modelPackage.getFile(entryDir ? `${entryDir}/${entryStem}.${extension}` : `${entryStem}.${extension}`);
    if (siblingMatch) {
      return siblingMatch;
    }

    const companionFiles = modelPackage.findFilesByExtension(extension);
    if (!companionFiles.length) return null;

    const sameDirectoryMatch = companionFiles.find((file) => ModelPackageUtils.getDirname(file.path) === entryDir);
    return sameDirectoryMatch || companionFiles[0];
  }

  /**
   * Unified entrypoint for direct model files and zip-backed multi-file packages.
   */
  public async load3DModel(
    source: ModelLoadSource,
    isArtwork: boolean,
    renderer?: THREE.WebGLRenderer
  ) {
    const modelPackage = await createModelPackage(source);
    if (!modelPackage) {
      console.warn("Loader: Unable to resolve model package", describeModelSource(source));
      return null;
    }

    try {
      const loadModel = this.modelLoaderRegistry[modelPackage.rootType];
      if (!loadModel) {
        console.warn(`Loader: Unsupported 3D model format ${modelPackage.rootType}`, {
          entryFile: modelPackage.entryFile.path
        });
        return null;
      }

      return await loadModel(modelPackage, isArtwork, renderer);
    } finally {
      modelPackage.dispose();
    }
  }

  public async loadGLTF(url: string, isArtwork: boolean) {
    return this.load3DModel(url, isArtwork);
  }

  public async loadFBX(url: string, isArtwork: boolean) {
    return this.load3DModel({
      type: "fbx",
      url,
    }, isArtwork);
  }

  public async loadObj(url: string, isArtwork: boolean, materialFile?: string) {
    return this.load3DModel({
      type: "obj",
      url,
      materialFile,
    }, isArtwork);
  }

  public async loadDAE(url: string, isArtwork: boolean) {
    return this.load3DModel({
      type: "dae",
      url,
    }, isArtwork);
  }

  public async loadPLY(url: string, isArtwork: boolean) {
    return this.load3DModel({
      type: "ply",
      url,
    }, isArtwork);
  }

  public async load3DS(url: string, isArtwork: boolean) {
    return this.load3DModel({
      type: "3ds",
      url,
    }, isArtwork);
  }

  private async loadGLTFPackage(
    modelPackage: ModelPackage,
    _isArtwork: boolean,
    renderer?: THREE.WebGLRenderer
  ) {
    const manager = this.cloneManager();
    const { gltfLoader, dracoLoader, ktx2Loader } = this.createGLTFLoader(manager, renderer);
    const cacheBypassKey = createMutableAssetCacheBypassKey();

    try {
      gltfLoader.setResourcePath(this.getVirtualResourcePath(modelPackage.entryFile.path));
      const entryUrl = modelPackage.entryUrl
        ? getMutableManagedAssetUrl(modelPackage.entryUrl, cacheBypassKey)
        : null;
      if (!entryUrl) {
        throw new Error(`Missing GLTF entry URL for ${modelPackage.entryFile.path}`);
      }

      return await this.withPackageUrlResolution(modelPackage, manager, cacheBypassKey, async () => {
        const gltf = await this.loadWithLoader<any>("GLTFLoader", entryUrl, (resolve, reject) => {
          gltfLoader.load(entryUrl, resolve, undefined, reject);
        });

        return this.finalizeLoadedModel(gltf.scene, modelPackage.rootType, gltf.animations);
      });
    } finally {
      dracoLoader.dispose();
      ktx2Loader?.dispose();
    }
  }

  private async loadFBXPackage(modelPackage: ModelPackage, _isArtwork: boolean) {
    const manager = this.cloneManager();
    const fbxLoader = new FBXLoader(manager);
    fbxLoader.setResourcePath(this.getVirtualResourcePath(modelPackage.entryFile.path));
    const cacheBypassKey = createMutableAssetCacheBypassKey();

    const entryUrl = modelPackage.entryUrl
      ? getMutableManagedAssetUrl(modelPackage.entryUrl, cacheBypassKey)
      : null;
    if (!entryUrl) {
      throw new Error(`Missing FBX entry URL for ${modelPackage.entryFile.path}`);
    }

    return this.withPackageUrlResolution(modelPackage, manager, cacheBypassKey, async () => {
      const fbx = await this.loadWithLoader<any>("FBXLoader", entryUrl, (resolve, reject) => {
        fbxLoader.load(entryUrl, resolve, undefined, reject);
      });

      return this.finalizeLoadedModel(fbx, "fbx", fbx.animations);
    });
  }

  private async loadOBJPackage(modelPackage: ModelPackage, _isArtwork: boolean) {
    const manager = this.cloneManager();
    const objLoader = new OBJLoader(manager);
    objLoader.setPath("");
    const cacheBypassKey = createMutableAssetCacheBypassKey();

    const entryUrl = modelPackage.entryUrl
      ? getMutableManagedAssetUrl(modelPackage.entryUrl, cacheBypassKey)
      : null;
    if (!entryUrl) {
      throw new Error(`Missing OBJ entry URL for ${modelPackage.entryFile.path}`);
    }

    return this.withPackageUrlResolution(modelPackage, manager, cacheBypassKey, async () => {
      const materialFile = this.findCompanionFile(modelPackage, modelPackage.entryFile, "mtl", modelPackage.preferredMaterialFile);

      if (materialFile) {
        const mtlLoader = new MTLLoader(manager);
        mtlLoader.setResourcePath(this.getVirtualResourcePath(materialFile.path));

        const rawMaterialUrl = modelPackage.getFileUrl(materialFile.path);
        const materialUrl = rawMaterialUrl
          ? getMutableManagedAssetUrl(rawMaterialUrl, cacheBypassKey)
          : null;
        if (materialUrl) {
          const materials = await this.loadWithLoader<any>("MTLLoader", materialUrl, (resolve, reject) => {
            mtlLoader.load(materialUrl, resolve, undefined, reject);
          });
          materials.preload();
          objLoader.setMaterials(materials);
        }
      }

      const obj = await this.loadWithLoader<any>("OBJLoader", entryUrl, (resolve, reject) => {
        objLoader.load(entryUrl, resolve, undefined, reject);
      });

      return this.finalizeLoadedModel(obj, "obj");
    });
  }

  private async loadDAEPackage(modelPackage: ModelPackage, _isArtwork: boolean) {
    const manager = this.cloneManager();
    const colladaLoader = new ColladaLoader(manager);
    colladaLoader.setResourcePath(this.getVirtualResourcePath(modelPackage.entryFile.path));
    const cacheBypassKey = createMutableAssetCacheBypassKey();

    const entryUrl = modelPackage.entryUrl;
    if (!entryUrl) {
      throw new Error(`Missing DAE entry URL for ${modelPackage.entryFile.path}`);
    }

    return this.withPackageUrlResolution(modelPackage, manager, cacheBypassKey, async () => {
      const colladaText = await modelPackage.readText(modelPackage.entryFile.path);
      if (!colladaText) {
        throw new Error(`Unable to read DAE model text: ${modelPackage.entryFile.path}`);
      }

      const normalizedColladaText = this.normalizeColladaDocument(colladaText);
      const collada = colladaLoader.parse(normalizedColladaText, this.getVirtualResourcePath(modelPackage.entryFile.path));

      return this.finalizeLoadedModel(collada.scene, "dae", collada.scene?.animations || []);
    });
  }

  private async loadPLYPackage(modelPackage: ModelPackage, _isArtwork: boolean) {
    const manager = this.cloneManager();
    const plyLoader = this.createPLYLoader(manager);

    const entryUrl = modelPackage.entryUrl;
    if (!entryUrl) {
      throw new Error(`Missing PLY entry URL for ${modelPackage.entryFile.path}`);
    }

    const arrayBuffer = await modelPackage.readArrayBuffer(modelPackage.entryFile.path);
    if (!arrayBuffer) {
      throw new Error(`Unable to read PLY model data: ${modelPackage.entryFile.path}`);
    }

    const plyHeader = this.inspectPLYHeader(arrayBuffer);
    const geometry = plyLoader.parse(arrayBuffer);

    if (!this.geometryHasFinitePositions(geometry)) {
      throw new Error(`PLY model contains invalid position data: ${modelPackage.entryFile.path}`);
    }

    const object = this.createPLYRenderable(geometry, plyHeader);
    return this.finalizeLoadedModel(object, "ply");
  }

  private async loadSTLPackage(modelPackage: ModelPackage, _isArtwork: boolean) {
    const manager = this.cloneManager();
    const stlLoader = new STLLoader(manager);
    const arrayBuffer = await modelPackage.readArrayBuffer(modelPackage.entryFile.path);
    if (!arrayBuffer) {
      throw new Error(`Unable to read STL model data: ${modelPackage.entryFile.path}`);
    }

    const geometry = stlLoader.parse(arrayBuffer);
    if (!this.geometryHasFinitePositions(geometry)) {
      throw new Error(`STL model contains invalid position data: ${modelPackage.entryFile.path}`);
    }

    if (!geometry.getAttribute("normal")) {
      geometry.computeVertexNormals();
    }
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    const hasVertexColors = Boolean(
      (geometry as any).hasColors && geometry.getAttribute("color")
    );
    const sourceAlpha = Number((geometry as any).alpha);
    const opacity = Number.isFinite(sourceAlpha)
      ? Math.min(1, Math.max(0, sourceAlpha))
      : 1;
    const model = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({
        color: hasVertexColors ? 0xffffff : 0xbfbfbf,
        vertexColors: hasVertexColors,
        opacity,
        transparent: opacity < 1,
      })
    );

    return this.finalizeLoadedModel(model, "stl");
  }

  private async load3DSPackage(modelPackage: ModelPackage, _isArtwork: boolean) {
    const manager = this.cloneManager();
    const tdsLoader = new TDSLoader(manager);
    tdsLoader.setResourcePath(this.getVirtualResourcePath(modelPackage.entryFile.path));
    const cacheBypassKey = createMutableAssetCacheBypassKey();

    const entryUrl = modelPackage.entryUrl
      ? getMutableManagedAssetUrl(modelPackage.entryUrl, cacheBypassKey)
      : null;
    if (!entryUrl) {
      throw new Error(`Missing 3DS entry URL for ${modelPackage.entryFile.path}`);
    }

    return this.withPackageUrlResolution(modelPackage, manager, cacheBypassKey, async () => {
      const model = await this.loadWithLoader<any>("TDSLoader", entryUrl, (resolve, reject) => {
        tdsLoader.load(entryUrl, resolve, undefined, reject);
      });

      return this.finalizeLoadedModel(model, "3ds");
    });
  }

  /**
   * Loads and processes HDR environment maps for scene lighting
   * @param url URL of the HDR file to load
   * @param renderer WebGL renderer instance for processing the environment map
   * @returns Promise resolving to processed environment map texture
   */
  public async loadHDR(url: string, renderer: THREE.WebGLRenderer) {
    try {
      if (!url) return null;

      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      this.withPmremShaderDiagnosticsSuppressed(renderer, () => {
        pmremGenerator.compileEquirectangularShader();
      });
      this.hdrLoader.setDataType(THREE.HalfFloatType);

      const toTexture = (parsed: any): THREE.Texture | null => {
        if (!parsed) return null;
        if (parsed.isTexture) {
          const width = Number(parsed.image?.width);
          const height = Number(parsed.image?.height);
          return Number.isFinite(width) && Number.isFinite(height) && width >= 4 && height >= 2
            ? parsed as THREE.Texture
            : null;
        }

        if (parsed.data && Number.isFinite(parsed.width) && Number.isFinite(parsed.height) && parsed.width >= 4 && parsed.height >= 2) {
          const texture = new THREE.DataTexture(
            parsed.data,
            parsed.width,
            parsed.height,
            THREE.RGBAFormat,
            parsed.type || THREE.HalfFloatType
          );
          texture.colorSpace = THREE.LinearSRGBColorSpace;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;
          texture.flipY = true;
          texture.needsUpdate = true;
          return texture;
        }
        return null;
      };

      try {
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} while loading HDR`);
        }

        const arrayBuffer = await response.arrayBuffer();
        let parsed: any = null;
        try {
          parsed = this.hdrLoader.parse(arrayBuffer);
        } catch (parseError) {
          throw new Error(`Invalid HDR format: ${parseError}`);
        }

        const hdrTexture = toTexture(parsed);
        if (!hdrTexture) {
          throw new Error("Invalid HDR texture loaded (unsupported parse result or invalid dimensions).");
        }

        const envMap = this.withPmremShaderDiagnosticsSuppressed(
          renderer,
          () => pmremGenerator.fromEquirectangular(hdrTexture).texture
        );
        if ((hdrTexture as any).dispose) {
          (hdrTexture as any).dispose();
        }
        return envMap;
      } catch (error) {
        const msg = String(error);
        if (msg.includes("Invalid HDR format")) {
          console.warn("HDRManager: Invalid HDR file format", { url: redactAssetUrl(url), error: sanitizeAssetError(error) });
        } else {
          console.error("HDRManager: Error in loadHDRTexture", sanitizeAssetError(error));
        }
        throw new Error(`Failed to load HDR texture from ${redactAssetUrl(url)}`);
      } finally {
        pmremGenerator.dispose();
      }

    } catch (error) {
      const msg = String(error);
      if (msg.includes("Invalid HDR format")) {
        console.warn("Loader: Invalid HDR format", { url: redactAssetUrl(url) });
      } else {
        console.error("Loader: Error loading HDR", { url: redactAssetUrl(url), error: sanitizeAssetError(error) });
      }
      throw error;
    }
  }

  /**
   * Loads data using axios HTTP client
   * @param url URL to fetch data from
   * @returns Promise resolving to the fetched data
   */
  public async loadAxios(url: string) {
    try {
      const res = await axios.get(url);
      return res.data;
    } catch (err) {
      console.error("Loader: Axios asset load failed", { url: redactAssetUrl(url), error: sanitizeAssetError(err) });
      throw err;
    }
  }

  /**
   * Loads image textures
   * @param url URL of the image to load
   * @returns Promise resolving to the loaded texture
   */
  public async loadTexture(url: string, options: { cache?: boolean } = {}) {
    try {
      const tex = await loadTexture(this.ensureTextureLoader(), url, this.cachedTextures, options);
      return tex;
    } catch (error) {
      console.error("Loader: Error loading texture", { url: redactAssetUrl(url), error: sanitizeAssetError(error) });
      throw error;
    }
  }

  /**
   * Loads and applies a texture to a material property
   * Sets up texture properties like wrapping and encoding
   * @param child Object to apply texture to
   * @param mapType Material property to set (e.g., "map", "normalMap")
   * @param texturePath Path to the texture file
   */
  public async loadTextureWithProps(child: any, mapType: string, texturePath: string): Promise<void> {
    try {
      await loadTextureWithProps(child, mapType, texturePath, this.ensureTextureLoader(), this.cachedTextures);
    } catch (error) {
      console.error("Loader: Error loading texture with props", { url: redactAssetUrl(texturePath), error: sanitizeAssetError(error) });
    }
  }

  /**
   * Loads audio files
   * @param url URL of the audio file to load
   * @returns Promise resolving to the loaded audio buffer
   */
  public async loadSound(url: string) {
    return await new Promise((resolve, reject) => {
      this.audioLoader.load(url, (sound) => {
        resolve(sound);
      }, () => { }, (err) => {
        console.error("Loader: Error in audioLoader.load", { url: redactAssetUrl(url), error: sanitizeAssetError(err) });
        reject(err);
      });
    })
  }

  /**
   * Loads LUT (Look-Up Table) cube files for color grading
   * @param url URL of the LUT cube file to load
   * @returns Promise resolving to the loaded LUT data
   */
  public async loadLUTCube(url: string) {
    try {
      return await new Promise((resolve, reject) => {
        if (url && url.length > 0) {
          this.lutCubeLoader.load(url, (lut) => {
            resolve(lut);
          }, undefined, (err) => {
            console.error("Loader: Error in lutCubeLoader.load", { url: redactAssetUrl(url), error: sanitizeAssetError(err) });
            reject(err);
          });
        } else {
          resolve(null);
        }
      });
    } catch (error) {
      console.error("Loader: Error loading LUT cube", { url: redactAssetUrl(url), error: sanitizeAssetError(error) });
      throw error;
    }
  }

  /**
   * Disposes cached textures and marks loader as unusable.
   */
  public dispose() {
    Object.values(this.cachedTextures).forEach((tex: any) => {
      if (tex?.dispose) {
        tex.dispose();
      }
    });
    this.cachedTextures = {};

    this.textureLoader = null;
    // @ts-ignore
    this.hdrLoader = null;
    // @ts-ignore
    this.audioLoader = null;
    // @ts-ignore
    this.lutCubeLoader = null;

    if (Loader.instance === this) {
      Loader.instance = null;
    }
  }
}
